import { getEditor, useEditor } from "@/utils/useEditor"
import { useAtomValue, useSetAtom } from "jotai"
import { useTranslation } from "react-i18next"
import { currentAdjustmentsAtom, saveCurrentAdjustmentState } from "@/store/store-history"
import { cropAspectLockAtom } from "@/components/Crop/crop-ui-state"
import { useEffect, useState } from "react"
import { fitToSize } from "@/core/utils/resize-image"
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/Select"
import equal from "fast-deep-equal"
import { vec2 } from "gl-matrix"
import { Crop, CropRatio } from "@/core/types"
import { showingCropPanelAtom } from "@/store/ui-views-state"
import { getDefaultAdjustments } from "@/core/adjustments"
import { constrainCrop } from "./crop-interaction-handlers"

const defaultRatios: Ratio[] = [
	[1, 1],
	[1, 2],
	[1, 3],
	[2, 3],
	[3, 4],
	[4, 5],
	[5, 7],
	[8.5, 11],
	[9, 16],
	[10, 16],
]

type Ratio = [number, number]
type AspectRatio = string // | "Custom" | "Original"

const formatRatio = (ratio: Ratio) => {
	return `${ratio[0]} x ${ratio[1]}`
}

const parseRatio = (aspectRatio: AspectRatio): AspectRatio | Ratio => {
	const parts = aspectRatio.split(" x ")

	if (parts.length === 2) {
		return [Number(parts[0]) || 1, Number(parts[1]) || 1]
	}

	return aspectRatio
}

const getCropRatio = (aspectRatio: AspectRatio | Ratio): CropRatio => {
	return typeof aspectRatio === "string" ? [0, 0] : aspectRatio
}

const normalizeAspectRatio = (value: AspectRatio, ratioOptions: string[]): AspectRatio => {
	if (value === "Custom" || value === "Original") {
		return value
	}

	if (ratioOptions.includes(value)) {
		return value
	}

	const parsed = parseRatio(value)
	if (Array.isArray(parsed)) {
		const inverse = formatRatio([parsed[1], parsed[0]])
		if (ratioOptions.includes(inverse)) {
			return inverse
		}
	}

	return "Custom"
}

export const applyCropRatio = (crop: Crop, ratio: number, fitToImage = false): Crop => {
	const editor = getEditor()
	const [imageWidth, imageHeight] = editor.imageSize
	const [cropWidth, cropHeight] = editor.croppedImageSize

	const height = ratio
	const width = 1

	let targetSize: vec2 = [cropWidth, cropHeight]

	if (fitToImage) {
		const maxCropDimension = Math.max(cropWidth, cropHeight)
		targetSize = [
			Math.min(imageWidth, maxCropDimension),
			Math.min(imageHeight, maxCropDimension),
		]
	}

	const [newWidth, newHeight] = fitToSize([width, height], targetSize)

	const deltaWidth = cropWidth - newWidth
	const deltaHeight = cropHeight - newHeight

	return [
		(crop[0] += deltaWidth / 2 / imageWidth),
		(crop[1] += deltaHeight / 2 / imageHeight),
		newWidth / imageWidth,
		newHeight / imageHeight,
	]
}

export const AspectRatioMenu = () => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const isCropActive = useAtomValue(showingCropPanelAtom)
	const adjustments = useAtomValue(currentAdjustmentsAtom)
	const aspectLock = useAtomValue(cropAspectLockAtom)
	const setAspectLock = useSetAtom(cropAspectLockAtom)
	const [aspectRatio, setAspectRatio] = useState<AspectRatio>("Original")
	const [isExpanded, setIsExpanded] = useState(false)

	const [cropWidth, cropHeight] = editor.croppedImageSize
	const isPortrait = cropHeight > cropWidth

	const ratios = defaultRatios.map((ratio) =>
		formatRatio(isPortrait ? [ratio[1], ratio[0]] : ratio),
	)

	useEffect(() => {
		const [w, h] = adjustments.cropRatio
		const defaultAdjustments = getDefaultAdjustments()

		let nextAspectRatio: AspectRatio | null = null

		// 1) No stored ratio: show Original when crop matches defaults, otherwise Custom.
		if (!w || !h) {
			const isOriginal = equal(adjustments.crop, defaultAdjustments.crop)
			nextAspectRatio = isOriginal ? "Original" : "Custom"
		}

		if (nextAspectRatio === null) {
			const [imageWidth, imageHeight] = editor.imageSize
			const cropWidth = adjustments.crop[2] * imageWidth
			const cropHeight = adjustments.crop[3] * imageHeight

			if (!cropWidth || !cropHeight) {
				return
			}

			const cropRatio = cropWidth / cropHeight
			const selectedRatio = w / h
			const inverseSelectedRatio = h / w
			const ratioDelta = Math.min(
				Math.abs(cropRatio - selectedRatio),
				Math.abs(cropRatio - inverseSelectedRatio),
			)

			// If the crop ratio doesn't match the stored ratio (0.01 tolerance for float drift).
			const isMismatch = ratioDelta > 0.01

			// 2) Locked: show the stored ratio only when it matches the live crop.
			// If the user free-cropped before re-locking, keep the selector on Custom.
			if (aspectLock) {
				nextAspectRatio = isMismatch ? "Custom" : formatRatio(adjustments.cropRatio)
			} else {
				// 3) Unlocked: if the live crop box drifts from the stored ratio, show Custom.
				nextAspectRatio = isMismatch ? "Custom" : aspectRatio
			}
		}

		if (nextAspectRatio !== null) {
			const normalized = normalizeAspectRatio(nextAspectRatio, ratios)
			if (normalized !== aspectRatio) {
				setAspectRatio(normalized)
			}
		}
	}, [
		aspectLock,
		isPortrait,
		adjustments.crop[0],
		adjustments.crop[1],
		adjustments.crop[2],
		adjustments.crop[3],
		adjustments.cropRatio[0] ?? 0,
		adjustments.cropRatio[1] ?? 0,
		editor.imageSize[0],
		editor.imageSize[1],
	])

	useEffect(() => {
		if (!isCropActive) {
			setIsExpanded(false)
		}
	}, [isCropActive])

	const updateAspectRatio = (aspectRatio: AspectRatio) => {
		setAspectRatio(aspectRatio)

		if (aspectRatio === "Custom") {
			setAspectLock(false)
			return
		}

		setAspectLock(true)

		const parsedRatio = parseRatio(aspectRatio)
		const cropRatio = getCropRatio(parsedRatio)
		let crop: Crop = [...adjustments.crop]

		if (aspectRatio === "Original") {
			crop = [0, 0, 1, 1]
		}

		if (Array.isArray(parsedRatio)) {
			crop = applyCropRatio(crop, parsedRatio[0] / parsedRatio[1])
		}

		crop = constrainCrop(
			{ ...editor.adjustments, crop, cropRatio },
			editor.imageSize,
			editor.initialOrientation,
		)

		editor.setAdjustments({ crop, cropRatio })

		saveCurrentAdjustmentState({
			message: "Crop",
		})
	}

	return (
		<Select
			trackingContext="crop_panel"
			trackingLabel="aspect_ratio"
			value={aspectRatio}
			onValueChange={(value) => {
				updateAspectRatio(value)
			}}
			open={isExpanded}
			onOpenChange={(open) => setIsExpanded(open)}
		>
			<SelectTrigger aria-label={t("aspectRatioMenu.ariaSelectCropAspectRatio")}>
				<div className="mr-2 text-base-400">{t("aspectRatioMenu.aspect")}</div>
				<SelectValue />
			</SelectTrigger>
			<SelectContent aria-label={t("aspectRatioMenu.ariaCropAspectRatioMenu")}>
				<SelectGroup>
					<SelectItem value="Custom">{t("aspectRatioMenu.custom")}</SelectItem>
					<SelectItem value="Original">{t("aspectRatioMenu.original")}</SelectItem>
					<SelectSeparator />
					{ratios.map((ratio) => (
						<SelectItem key={ratio} value={ratio}>
							{ratio}
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	)
}
